import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Terminal, Lock, CheckCircle, Fingerprint, MousePointer2, Loader2, Database, ShieldCheck, Clock } from 'lucide-react';
import { withKinematicSensors } from '../../core/hocs/withKinematicSensors';

function ZeroKnowledgeSandboxBase({ resetKinematics, getKinematicMetrics }) {
  const [isBotMode, setIsBotMode] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle', 'submitting', 'minted', 'quarantined'
  const [mockJwt, setMockJwt] = useState(null);
  
  // Isolated state exclusively for the visual demonstration panel
  const [visualMetrics, setVisualMetrics] = useState({ jiggleCount: 0, ttfi: null });

  // Polling to update the UI visuals without triggering massive un-optimized cascaded state renders
  useEffect(() => {
    const interval = setInterval(() => {
        if (status === 'idle' && !isBotMode) {
            const metrics = getKinematicMetrics();
            setVisualMetrics({
                jiggleCount: metrics.jiggleCount,
                ttfi: metrics.ttfi
            });
        }
    }, 200);
    return () => clearInterval(interval);
  }, [status, isBotMode, getKinematicMetrics]);

  useEffect(() => {
    window.__PUPPETEER_ACTIVE__ = isBotMode;
    return () => { window.__PUPPETEER_ACTIVE__ = false; };
  }, [isBotMode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isBotMode) {
      setStatus('quarantined');
      return;
    }

    setStatus('submitting');
    
    const metrics = getKinematicMetrics();
    const finalJiggle = metrics.jiggleCount || 0;
    const finalTtfi = metrics.ttfi || 0;

    // Simulate SDK Transmission & Server-Side Scoring
    setTimeout(() => {
      const generatedVci = 1250 + (finalJiggle * 5);
      const generatedMtbf = finalTtfi ? Math.round(finalTtfi * 1.5) : 800;
      const generatedAdaptability = Math.max(0, Math.min(100, 85 - Math.round(finalJiggle / 2)));
      
      const payloadObj = {
          uid: "cand_0x9A4b2e8C1F",
          vciScore: generatedVci,
          adaptabilityScore: generatedAdaptability,
          mtbf: generatedMtbf,
          auditFlag: "Zero-Knowledge Sandbox Execution",
          iat: Math.floor(Date.now() / 1000),
          exp: Math.floor(Date.now() / 1000) + 900 // 15 Minute Lifespan
      };

      // Mock Cryptographic Minting
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify(payloadObj));
      const signature = "E9a_3X-pLZqQ0sVzB_m7K";
      
      setMockJwt({
          token: `${header}.${payload}.${signature}`,
          decoded: payloadObj
      });
      setStatus('minted');
    }, 2500);
  };

  const handleReset = () => {
    setStatus('idle');
    setMockJwt(null);
    resetKinematics();
    setVisualMetrics({ jiggleCount: 0, ttfi: null });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans p-6 md:p-12 overflow-hidden flex flex-col">
      
      <div className="w-full max-w-6xl mx-auto flex items-center justify-between mb-8 pb-6 border-b border-slate-800">
          <div>
              <h1 className="text-2xl font-black text-white uppercase tracking-widest flex items-center gap-3">
                  <ShieldCheck className="text-emerald-500" /> Enterprise Zero-Trust Sandbox
              </h1>
              <p className="text-xs font-mono text-slate-500 mt-2">M&A Technical Audit Environment // PII Stripping Active</p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-slate-900 px-4 py-2 rounded-lg border border-slate-800 font-mono text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Sensors Armed
          </div>
      </div>

      <div className="flex-1 w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl relative overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none"><Database size={150} /></div>
              
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2 relative z-10">
                  <Fingerprint className="text-blue-500" /> Mock Application Form
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5 relative z-10 flex-1">
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Legal Name (PII)</label>
                      <input type="text" disabled={status !== 'idle'} required placeholder="John Doe" className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white focus:border-blue-500 outline-none transition-colors" />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email Address (PII)</label>
                      <input type="email" disabled={status !== 'idle'} required placeholder="candidate@example.com" className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white focus:border-blue-500 outline-none transition-colors" />
                  </div>
                  
                  <div className="p-4 bg-slate-950/50 border border-slate-800 rounded-xl mt-6">
                      <div className="flex items-center gap-3">
                          <input 
                              type="checkbox" 
                              id="botToggle" 
                              checked={isBotMode} 
                              onChange={(e) => setIsBotMode(e.target.checked)} 
                              disabled={status !== 'idle'}
                              className="w-4 h-4 accent-red-600"
                          />
                          <label htmlFor="botToggle" className="text-sm font-bold text-slate-300 cursor-pointer">
                              Simulate Bot Execution (Linear Coordinates)
                          </label>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 ml-7 italic">Injects `window.__puppeteer` intercept into the environment payload.</p>
                  </div>

                  <div className="pt-6 mt-auto">
                      <button 
                          type="submit" 
                          disabled={status !== 'idle'}
                          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-black uppercase tracking-widest py-4 rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                      >
                          Submit Application
                      </button>
                  </div>
              </form>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl flex flex-col font-mono relative overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
                  <h2 className="text-sm font-bold text-slate-400 uppercase flex items-center gap-2">
                      <Terminal size={16} /> jobvantage-sdk.js Stream
                  </h2>
                  <button onClick={handleReset} className="text-xs text-slate-500 hover:text-white transition-colors">RESET STATE</button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl">
                      <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 flex items-center gap-1"><MousePointer2 size={12}/> Volatility (Jiggle)</p>
                      <p className={`text-3xl font-black ${isBotMode ? 'text-slate-700' : 'text-emerald-400'}`}>{isBotMode ? '0' : visualMetrics.jiggleCount}</p>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl">
                      <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 flex items-center gap-1"><Clock size={12}/> TTFI Latency</p>
                      <p className={`text-3xl font-black ${isBotMode ? 'text-slate-700' : 'text-cyan-400'}`}>{isBotMode ? '0ms' : visualMetrics.ttfi === null ? 'Awaiting...' : `${visualMetrics.ttfi}ms`}</p>
                  </div>
              </div>

              <div className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-6 overflow-hidden flex flex-col relative">
                  <AnimatePresence mode="wait">
                      {status === 'idle' && (
                          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="m-auto text-center opacity-50">
                              <Lock size={48} className="mx-auto mb-4 text-slate-700" />
                              <p className="text-sm text-slate-500">Awaiting Submittal Intercept...</p>
                          </motion.div>
                      )}

                      {status === 'submitting' && (
                          <motion.div key="submitting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="m-auto text-center">
                              <Loader2 size={48} className="mx-auto mb-4 text-blue-500 animate-spin" />
                              <p className="text-sm font-bold text-blue-400 uppercase tracking-widest animate-pulse">Minting Provenance Token...</p>
                              <p className="text-xs text-slate-500 mt-2">Executing strict PII severing protocol.</p>
                          </motion.div>
                      )}

                      {status === 'quarantined' && (
                          <motion.div key="quarantined" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="absolute inset-0 bg-red-950/90 z-10 flex flex-col items-center justify-center border-4 border-red-600 rounded-xl p-6 text-center">
                              <ShieldAlert size={64} className="text-red-500 mb-4" />
                              <h3 className="text-2xl font-black text-red-500 uppercase tracking-widest mb-2">QUARANTINE</h3>
                              <p className="text-red-300 font-bold text-sm mb-4">Synthetic Agent Detected.</p>
                              <p className="text-xs text-red-400/70 max-w-xs leading-relaxed">The SDK intercepted a headless browser signature. Connection severed. Database writes blocked.</p>
                          </motion.div>
                      )}

                      {status === 'minted' && mockJwt && (
                          <motion.div key="minted" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="h-full flex flex-col">
                              <div className="flex items-center gap-2 text-emerald-500 mb-4 pb-4 border-b border-slate-800">
                                  <CheckCircle size={18} />
                                  <span className="text-xs font-bold uppercase tracking-widest">JWT MINTED // PII STRIPPED</span>
                              </div>
                              
                              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-4">
                                  <div>
                                      <p className="text-[10px] text-slate-500 font-bold mb-1">Encoded Provenance Hash</p>
                                      <div className="bg-black/50 p-3 rounded-lg border border-slate-800 text-[10px] text-emerald-500/50 break-all leading-relaxed">
                                          {mockJwt.token}
                                      </div>
                                  </div>
                                  
                                  <div>
                                      <p className="text-[10px] text-slate-500 font-bold mb-1">Decoded Payload (Sent to ATS)</p>
                                      <pre className="bg-blue-950/30 p-4 rounded-lg border border-blue-900/50 text-[11px] text-blue-300 overflow-x-auto whitespace-pre-wrap leading-relaxed shadow-inner">
                                          {JSON.stringify(mockJwt.decoded, null, 2)}
                                      </pre>
                                  </div>
                              </div>
                          </motion.div>
                      )}
                  </AnimatePresence>
              </div>
          </div>
      </div>
    </div>
  );
}

export default withKinematicSensors(ZeroKnowledgeSandboxBase);